import React, { useContext, useMemo, useState } from 'react';

import { useConfig } from '../../../../defaultConfig';
import { avtalegiroSelector, useAccounts } from '../../../../fetchHooks/useAccount';
import { PaymentAccountState, usePaymentFromAccount } from '../../../../hooks/useAccountState';
import { useForm, Form, CreateForm } from '../../../../hooks/useForm';
import { getRedirectLinkAvtalegiro } from '../mapper';
import { PaymentAction, PaymentActionType } from './actions';
import { PaymentState } from './state';
import { PaymentError } from './validate';

type PaymentFormWithUtils = {
    redirectLinkAvtalegiro?: string;
    searchString: [string, React.Dispatch<React.SetStateAction<string>>];
    showAddNewReciever: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
} & Form<PaymentState, PaymentAction, PaymentError, PaymentActionType>;

export const PaymentContext = React.createContext<PaymentFormWithUtils | undefined>(undefined);

export default () => {
    const context = useContext(PaymentContext)! as PaymentFormWithUtils;
    if (context === undefined) {
        throw new Error('useFormContext must be used within PaymentContext');
    }

    return context;
};

export const PaymentFormContext = ({
    validateHook,
    reducer,
    initialState,
    children,
}: CreateForm<PaymentState, PaymentAction, PaymentError>) => {
    const { avtalegiroLink } = useConfig();

    const searchString = useState('');
    const showAddNewReciever = useState(false);

    const form = useForm<PaymentState, PaymentAction, PaymentError, PaymentActionType>(
        validateHook,
        reducer,
        initialState,
    );

    const paymentFromAccount = usePaymentFromAccount(form.state.fromAccount);
    const { data: avtalegiroAccounts = [] } = useAccounts({ select: avtalegiroSelector });

    const redirectLinkAvtalegiro = useMemo(
        () =>
            getRedirectLinkAvtalegiro(
                form.state,
                paymentFromAccount.targetFromAccount,
                avtalegiroAccounts.length > 0,
                avtalegiroLink,
            ),
        [form.state, paymentFromAccount.targetFromAccount, avtalegiroAccounts?.length, avtalegiroLink],
    );
    const contextValue = useMemo(
        () => ({ redirectLinkAvtalegiro, searchString, showAddNewReciever, ...form }),
        [redirectLinkAvtalegiro, searchString, showAddNewReciever, form],
    );

    return (
        <PaymentContext.Provider value={contextValue}>
            <PaymentAccountState.Provider value={paymentFromAccount}>{children}</PaymentAccountState.Provider>
        </PaymentContext.Provider>
    );
};
